import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface PricingDefaults {
  id: string;
  currency: string;
  subscriptionFeeUsd: number;
  subscriptionDiscountPct: number;
  includedDiscountPct: number;
  usageDiscountPct: number;
  profitMarginPct: number;
  freeSubscriptionMonths: number;
  version: string;
  effectiveFrom?: string;
  status: string;
  includedThresholdUsd: number;
}

export interface ExecutionPricing {
  id: string;
  currency: string;
  perVcpuHourUsd: number;
  perGbHourUsd: number;
  perSecondUsd: number;
  overheadMultiplier: number;
  version: string;
  effectiveFrom?: string;
  status: string;
}

export interface OrgPricingListItem {
  orgId: string;
  orgName: string;
  orgStatus: string;
  joinedAt?: string | null;
  subscriptionFeeUsd: number;
  includedThresholdUsd: number;
  effectiveSubscriptionChargedUsd: number;
  inFreePeriod: boolean;
  freeSubscriptionMonths: number;
  hasOverride: boolean;
}

export interface UnbilledPeriod {
  orgId: string;
  period: string;
  currency: string;
  periodTotalUsd: number;
  alreadyInvoicedUsd: number;
  unbilledSubscriptionUsd: number;
  unbilledUsageUsd: number;
  unbilledExecutionUsd: number;
  unbilledTotalUsd: number;
  cardOnFile: boolean;
  blockers: string[];
  inFreePeriod: boolean;
}

export interface BillingPreviewDigestStatus {
  recipientsConfigured: boolean;
  recipientCount: number;
}

export interface BillingPreviewDigestResult {
  status: string;
  source: string;
  period: string;
  orgCount: number;
  willChargeCount: number;
  unbilledTotalUsd: number;
  recipientsConfigured: boolean;
  recipientCount: number;
  reason?: string;
}

export interface StripeHealth {
  status: 'healthy' | 'not_configured' | 'unreachable' | string;
  mode?: string | null;
  secretLoaded: boolean;
  publishableKeyPresent: boolean;
  webhookSecretPresent: boolean;
  accountOk: boolean;
}

export interface OrgChargeResult {
  orgId: string;
  status: string;
  period: string;
  chargedUsd: number;
  alreadyInvoicedUsd: number;
  periodTotalUsd: number;
  invoiceId?: string | null;
  stripeInvoiceId?: string | null;
  hostedInvoiceUrl?: string | null;
}

export interface OrgPricingDetail {
  orgId: string;
  orgName: string;
  orgStatus: string;
  joinedAt?: string | null;
  inFreePeriod?: boolean;
  freeUntil?: string | null;
  effectiveSubscriptionChargedUsd?: number;
  resolved: {
    currency: string;
    subscriptionFeeUsd: number;
    subscriptionDiscountPct: number;
    includedDiscountPct: number;
    usageDiscountPct: number;
    profitMarginPct: number;
    freeSubscriptionMonths: number;
    hasOrgOverride: boolean;
    source: string;
    includedThresholdUsd: number;
  };
  override: Record<string, unknown> | null;
}

export interface StaffAnalyticsSummary {
  from: string;
  to: string;
  operatorView: boolean;
  tokensTotal: number;
  tokensIn: number;
  tokensOut: number;
  sessions: number;
  exchangeCount: number;
  activeOrgs: number;
  platformCostUsd: number;
  executionCostUsd?: number;
  totalPlatformCostUsd?: number;
  estEarningsUsd: number;
  marginUsd: number;
  organisations: Array<{
    orgId: string;
    orgName: string;
    tokensTotal: number;
    exchangeCount: number;
    sessions: number;
    platformCostUsd: number;
    executionCostUsd?: number;
    executionChargesUsd?: number;
    totalPlatformCostUsd?: number;
    estSubscriptionUsd: number;
    estUsageUsd: number;
    estEarningsUsd: number;
    marginUsd: number;
  }>;
}

export interface StaffTimeseriesResponse {
  from: string;
  to: string;
  grain: string;
  metric: string;
  points: Array<{ bucket: string; value: number }>;
}

export interface StaffBreakdownResponse {
  from: string;
  to: string;
  dimension: string;
  items: Array<{
    key: string;
    label?: string;
    tokensTotal: number;
    exchangeCount: number;
    platformCostUsd: number;
    estSubscriptionUsd?: number | null;
    estUsageUsd?: number | null;
    estEarningsUsd?: number | null;
  }>;
}

export interface OrgSearchItem {
  orgId: string;
  name: string;
  status: string;
  businessType?: string;
  memberCount: number;
}

export interface StaffAssumption {
  id: string;
  name: string;
  businessType?: string;
  status?: string;
  roleName?: string;
  assumed: boolean;
  expiresAt?: string;
}

export interface OrgImpactPerson {
  userId: string;
  email: string;
  cognitoSub: string;
}

export interface OrgImpact {
  orgId: string;
  name: string;
  status: string;
  memberCount: number;
  applicationsCount: number;
  agentsCount: number;
  apiKeyProfilesCount: number;
  hasPricingOverride: boolean;
  hasBudgetConfig: boolean;
  billingProfilePresent: boolean;
  cognitoUsersToDelete?: OrgImpactPerson[];
  usersRetained?: OrgImpactPerson[];
}

export interface OrgMemberItem {
  memberId: string;
  userId: string;
  name: string | null;
  email: string | null;
  role: string | null;
  status: string;
  userStatus: string | null;
}

export interface StaffUserItem {
  userId: string;
  email: string;
  name: string | null;
  status: string;
  organisations: string[];
}

export const staffApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPricingDefaults: builder.query<PricingDefaults, void>({
      query: () => '/portal/staff/pricing/defaults',
      transformResponse: (response: unknown) =>
        normalizeKeys<PricingDefaults>(response),
      providesTags: ['StaffPricing'],
    }),
    updatePricingDefaults: builder.mutation<
      PricingDefaults,
      {
        subscriptionFeeUsd: number;
        subscriptionDiscountPct: number;
        includedDiscountPct: number;
        usageDiscountPct: number;
        profitMarginPct: number;
        freeSubscriptionMonths: number;
        note: string;
        currency?: string;
      }
    >({
      query: (body) => ({
        url: '/portal/staff/pricing/defaults',
        method: 'PUT',
        body: {
          subscription_fee_usd: body.subscriptionFeeUsd,
          subscription_discount_pct: body.subscriptionDiscountPct,
          included_discount_pct: body.includedDiscountPct,
          usage_discount_pct: body.usageDiscountPct,
          profit_margin_pct: body.profitMarginPct,
          free_subscription_months: body.freeSubscriptionMonths,
          note: body.note,
          currency: body.currency || 'USD',
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<PricingDefaults>(response),
      invalidatesTags: ['StaffPricing'],
    }),
    getExecutionPricing: builder.query<ExecutionPricing, void>({
      query: () => '/portal/staff/pricing/executions',
      transformResponse: (response: unknown) =>
        normalizeKeys<ExecutionPricing>(response),
      providesTags: ['StaffPricing'],
    }),
    updateExecutionPricing: builder.mutation<
      ExecutionPricing,
      {
        perVcpuHourUsd: number;
        perGbHourUsd: number;
        perSecondUsd: number;
        overheadMultiplier: number;
        note: string;
        currency?: string;
      }
    >({
      query: (body) => ({
        url: '/portal/staff/pricing/executions',
        method: 'PUT',
        body: {
          per_vcpu_hour_usd: body.perVcpuHourUsd,
          per_gb_hour_usd: body.perGbHourUsd,
          per_second_usd: body.perSecondUsd,
          overhead_multiplier: body.overheadMultiplier,
          note: body.note,
          currency: body.currency || 'USD',
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<ExecutionPricing>(response),
      invalidatesTags: ['StaffPricing'],
    }),
    listOrgPricing: builder.query<
      { items: OrgPricingListItem[]; total: number },
      { q?: string } | void
    >({
      query: (args) => ({
        url: '/portal/staff/pricing/orgs',
        params: { q: args && 'q' in args ? args.q : undefined },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{ items: OrgPricingListItem[]; total: number }>(response),
      providesTags: ['StaffPricing'],
    }),
    getOrgPricing: builder.query<OrgPricingDetail, string>({
      query: (orgId) => `/portal/staff/pricing/orgs/${orgId}`,
      transformResponse: (response: unknown) =>
        normalizeKeys<OrgPricingDetail>(response),
      providesTags: (_r, _e, orgId) => [{ type: 'StaffPricing', id: orgId }],
    }),
    updateOrgPricing: builder.mutation<
      OrgPricingDetail,
      {
        orgId: string;
        subscriptionFeeUsd?: number | null;
        subscriptionDiscountPct?: number | null;
        includedDiscountPct?: number | null;
        usageDiscountPct?: number | null;
        profitMarginPct?: number | null;
        freeSubscriptionMonths?: number | null;
        note: string;
      }
    >({
      query: ({ orgId, note, ...fields }) => ({
        url: `/portal/staff/pricing/orgs/${orgId}`,
        method: 'PUT',
        body: {
          subscription_fee_usd: fields.subscriptionFeeUsd,
          subscription_discount_pct: fields.subscriptionDiscountPct,
          included_discount_pct: fields.includedDiscountPct,
          usage_discount_pct: fields.usageDiscountPct,
          profit_margin_pct: fields.profitMarginPct,
          free_subscription_months: fields.freeSubscriptionMonths,
          note,
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<OrgPricingDetail>(response),
      invalidatesTags: ['StaffPricing'],
    }),
    getUnbilledPeriod: builder.query<UnbilledPeriod, string>({
      query: (orgId) => `/portal/staff/billing/orgs/${orgId}/unbilled`,
      transformResponse: (response: unknown) =>
        normalizeKeys<UnbilledPeriod>(response),
      providesTags: (_r, _e, orgId) => [{ type: 'StaffPricing', id: orgId }],
    }),
    getBillingPreviewDigestStatus: builder.query<BillingPreviewDigestStatus, void>({
      query: () => '/portal/staff/billing/preview-digest',
      transformResponse: (response: unknown) =>
        normalizeKeys<BillingPreviewDigestStatus>(response),
    }),
    sendBillingPreviewDigest: builder.mutation<
      BillingPreviewDigestResult,
      { confirm: true }
    >({
      query: (body) => ({
        url: '/portal/staff/billing/preview-digest',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<BillingPreviewDigestResult>(response),
    }),
    getStripeHealth: builder.query<StripeHealth, void>({
      query: () => '/portal/staff/billing/stripe-health',
      transformResponse: (response: unknown) => normalizeKeys<StripeHealth>(response),
    }),
    chargeOrgPeriod: builder.mutation<
      OrgChargeResult,
      { orgId: string; period?: string; confirm: true }
    >({
      query: ({ orgId, period, confirm }) => ({
        url: `/portal/staff/billing/orgs/${orgId}/charge`,
        method: 'POST',
        body: { period, confirm },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<OrgChargeResult>(response),
      invalidatesTags: ['StaffPricing'],
    }),
    resetOrgPricing: builder.mutation<
      OrgPricingDetail,
      { orgId: string; note?: string }
    >({
      query: ({ orgId, note }) => ({
        url: `/portal/staff/pricing/orgs/${orgId}/reset`,
        method: 'POST',
        body: { note },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<OrgPricingDetail>(response),
      invalidatesTags: ['StaffPricing'],
    }),
    getStaffAnalyticsSummary: builder.query<
      StaffAnalyticsSummary,
      { from?: string; to?: string; orgId?: string } | void
    >({
      query: (args) => ({
        url: '/portal/staff/analytics/summary',
        params: {
          from: args && 'from' in args ? args.from : undefined,
          to: args && 'to' in args ? args.to : undefined,
          org_id: args && 'orgId' in args ? args.orgId : undefined,
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<StaffAnalyticsSummary>(response),
      providesTags: ['StaffAnalytics'],
    }),
    getStaffAnalyticsTimeseries: builder.query<
      StaffTimeseriesResponse,
      { from?: string; to?: string; grain?: string; metric?: string }
    >({
      query: (args) => ({
        url: '/portal/staff/analytics/timeseries',
        params: {
          from: args.from,
          to: args.to,
          grain: args.grain,
          metric: args.metric,
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<StaffTimeseriesResponse>(response),
      providesTags: ['StaffAnalytics'],
    }),
    getStaffAnalyticsBreakdown: builder.query<
      StaffBreakdownResponse,
      { from?: string; to?: string; dimension?: string }
    >({
      query: (args) => ({
        url: '/portal/staff/analytics/breakdown',
        params: {
          from: args.from,
          to: args.to,
          dimension: args.dimension || 'org',
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<StaffBreakdownResponse>(response),
      providesTags: ['StaffAnalytics'],
    }),
    searchStaffOrganisations: builder.query<
      { items: OrgSearchItem[]; total: number },
      { q?: string } | void
    >({
      query: (args) => ({
        url: '/portal/staff/organisations',
        params: { q: args && 'q' in args ? args.q : undefined },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{ items: OrgSearchItem[]; total: number }>(response),
    }),
    assumeOrgAdmin: builder.mutation<StaffAssumption, string>({
      query: (orgId) => ({
        url: `/portal/staff/organisations/${orgId}/assume-admin`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: unknown) => normalizeKeys<StaffAssumption>(response),
      invalidatesTags: ['UserOrganisations'],
    }),
    endOrgAssumption: builder.mutation<{ ended: boolean }, string>({
      query: (orgId) => ({
        url: `/portal/staff/organisations/${orgId}/assumption`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{ ended: boolean }>(response),
      invalidatesTags: ['UserOrganisations'],
    }),
    searchSuperOrganisations: builder.query<
      { items: OrgSearchItem[]; total: number },
      { q?: string } | void
    >({
      query: (args) => ({
        url: '/portal/super/organisations',
        params: { q: args && 'q' in args ? args.q : undefined },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{ items: OrgSearchItem[]; total: number }>(response),
    }),
    getOrganisationImpact: builder.query<OrgImpact, string>({
      query: (orgId) => `/portal/super/organisations/${orgId}/impact`,
      transformResponse: (response: unknown) => normalizeKeys<OrgImpact>(response),
    }),
    deleteOrganisation: builder.mutation<
      { orgId: string; name: string; status: string; deleted: boolean; message: string },
      { orgId: string; confirmName: string }
    >({
      query: ({ orgId, confirmName }) => ({
        url: `/portal/super/organisations/${orgId}`,
        method: 'DELETE',
        body: { confirm_name: confirmName },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{
          orgId: string;
          name: string;
          status: string;
          deleted: boolean;
          message: string;
        }>(response),
    }),
    wipeOrganisation: builder.mutation<
      { orgId: string; name: string; deleted: boolean; message: string },
      { orgId: string; confirmName: string }
    >({
      query: ({ orgId, confirmName }) => ({
        url: `/portal/super/organisations/${orgId}/wipe`,
        method: 'DELETE',
        body: { confirm_name: confirmName },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{
          orgId: string;
          name: string;
          deleted: boolean;
          message: string;
        }>(response),
    }),
    getOrgMembers: builder.query<
      { orgId: string; name: string; items: OrgMemberItem[]; total: number },
      string
    >({
      query: (orgId) => `/portal/staff/organisations/${orgId}/members`,
      transformResponse: (response: unknown) =>
        normalizeKeys<{
          orgId: string;
          name: string;
          items: OrgMemberItem[];
          total: number;
        }>(response),
    }),
    searchStaffUsers: builder.query<
      { items: StaffUserItem[]; total: number },
      { q?: string } | void
    >({
      query: (args) => ({
        url: '/portal/staff/users',
        params: { q: args && 'q' in args ? args.q : undefined },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{ items: StaffUserItem[]; total: number }>(response),
    }),
    softDeleteUser: builder.mutation<
      { userId: string; status: string },
      { userId: string; confirm: true }
    >({
      query: ({ userId, confirm }) => ({
        url: `/portal/staff/users/${userId}/soft-delete`,
        method: 'POST',
        body: { confirm },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{ userId: string; status: string }>(response),
    }),
    restoreUser: builder.mutation<
      { userId: string; status: string },
      { userId: string; confirm: true }
    >({
      query: ({ userId, confirm }) => ({
        url: `/portal/staff/users/${userId}/restore`,
        method: 'POST',
        body: { confirm },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{ userId: string; status: string }>(response),
    }),
    hardDeleteUser: builder.mutation<
      { userId: string; deleted: boolean; message: string },
      { userId: string; confirmEmail: string }
    >({
      query: ({ userId, confirmEmail }) => ({
        url: `/portal/staff/users/${userId}`,
        method: 'DELETE',
        body: { confirm_email: confirmEmail },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{ userId: string; deleted: boolean; message: string }>(
          response,
        ),
    }),
  }),
});

export const {
  useGetPricingDefaultsQuery,
  useUpdatePricingDefaultsMutation,
  useGetExecutionPricingQuery,
  useUpdateExecutionPricingMutation,
  useListOrgPricingQuery,
  useGetOrgPricingQuery,
  useUpdateOrgPricingMutation,
  useResetOrgPricingMutation,
  useGetUnbilledPeriodQuery,
  useGetBillingPreviewDigestStatusQuery,
  useSendBillingPreviewDigestMutation,
  useGetStripeHealthQuery,
  useChargeOrgPeriodMutation,
  useGetStaffAnalyticsSummaryQuery,
  useGetStaffAnalyticsTimeseriesQuery,
  useGetStaffAnalyticsBreakdownQuery,
  useSearchStaffOrganisationsQuery,
  useAssumeOrgAdminMutation,
  useEndOrgAssumptionMutation,
  useSearchSuperOrganisationsQuery,
  useLazySearchSuperOrganisationsQuery,
  useGetOrganisationImpactQuery,
  useLazyGetOrganisationImpactQuery,
  useDeleteOrganisationMutation,
  useWipeOrganisationMutation,
  useGetOrgMembersQuery,
  useSearchStaffUsersQuery,
  useSoftDeleteUserMutation,
  useRestoreUserMutation,
  useHardDeleteUserMutation,
} = staffApi;
